import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { Ornament } from "../components/Ornament";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin(nextPath) {
  const dest = nextPath && nextPath.startsWith("/") && !nextPath.startsWith("//") ? nextPath : "/dashboard";
  const redirectUrl = window.location.origin + dest;
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

const PASSWORD_MIN = 8;

function passwordChecks(pw) {
  return {
    length: pw.length >= PASSWORD_MIN,
    letter: /[A-Za-z]/.test(pw),
    number: /\d/.test(pw),
  };
}

// Turn an axios error into a friendly, actionable message.  Older readers
// need plain guidance ("your account already exists — sign in instead"), and
// we also surface a discreet status-code line so we can diagnose bug reports.
function describeAuthError(e, mode) {
  const resp = e?.response;
  const status = resp?.status;
  const detail = resp?.data?.detail;

  // No response at all — network dropped, DNS failed, or CORS blocked the reply.
  if (!resp) {
    const offline = typeof navigator !== "undefined" && navigator.onLine === false;
    return {
      message: offline
        ? "You appear to be offline. Please check your internet connection and try again."
        : "The server didn't respond. Please check your connection and try again in a moment.",
      offerSignIn: false,
      debug: offline ? "offline" : (e?.message || "network"),
    };
  }

  // Pydantic validation error — arrives as an array of {msg, loc, ...}.
  if (Array.isArray(detail)) {
    const first = detail[0] || {};
    const field = Array.isArray(first.loc) ? first.loc[first.loc.length - 1] : "";
    const msg = first.msg || "Please check your details and try again.";
    return { message: field ? `${msg} (${field})` : msg, offerSignIn: false, debug: `422 ${field || "validation"}` };
  }

  // 400 "Email already registered" — offer the sign-in shortcut.
  if (status === 400 && typeof detail === "string" && /already/i.test(detail) && mode === "register") {
    return {
      message: "That email already has a Living Book account. Please sign in instead.",
      offerSignIn: true,
      debug: "400 duplicate",
    };
  }

  // 401 on login = wrong password / unknown email.
  if (status === 401 && mode === "login") {
    return {
      message: "That email and password don't match. Please try again, or use a magic link if you've forgotten your password.",
      offerSignIn: false,
      debug: "401 login",
    };
  }

  // 429 rate limit.
  if (status === 429) {
    return {
      message: "Too many attempts just now. Please wait a minute and try again.",
      offerSignIn: false,
      debug: "429 rate-limit",
    };
  }

  // 5xx server errors.
  if (status >= 500) {
    return {
      message: "Our server hiccuped. Please try again in a moment — your details are safe.",
      offerSignIn: false,
      debug: `${status} server`,
    };
  }

  // Any string detail from the backend — trust it.
  if (typeof detail === "string" && detail.trim()) {
    return { message: detail, offerSignIn: false, debug: `${status || "?"}` };
  }

  // Fallback — still show status so bug reports carry a clue.
  return {
    message: "Something went wrong. Please try again, or use a magic link to sign in.",
    offerSignIn: false,
    debug: status ? `${status} unknown` : "unknown",
  };
}

// Only allow same-origin relative next paths to avoid open redirect.
function safeNext(next) {
  if (!next) return null;
  if (!next.startsWith("/")) return null;
  if (next.startsWith("//")) return null;
  return next;
}

export default function LoginPage() {
  const navigate = useNavigate();
  const { setUser, checkAuth } = useAuth();
  const [searchParams] = useSearchParams();
  const joinCode = (searchParams.get("join") || "").trim();
  const nextUrl = safeNext(searchParams.get("next"));
  const flow = (searchParams.get("flow") || "").trim() || null;
  const isStoryWeekFlow = flow === "story_week" || (nextUrl && nextUrl.startsWith("/story-week"));
  const isBetaFlow = flow === "founding_beta" || (nextUrl && nextUrl.startsWith("/founding-beta"));
  const [mode, setMode] = useState(joinCode || isStoryWeekFlow || isBetaFlow ? "register" : "login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [error, setError] = useState("");
  const [errorDetail, setErrorDetail] = useState("");
  const [errorOfferSignIn, setErrorOfferSignIn] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // Forgot-password mini-flow lives inline on the sign-in card.
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotError, setForgotError] = useState("");

  const pwChecks = useMemo(() => passwordChecks(password), [password]);
  const pwOk = pwChecks.length && pwChecks.letter && pwChecks.number;
  // After a Story Week registration we show a "Check your email" success state
  // instead of navigating away — the user must click the link in the email.
  const [registeredEmail, setRegisteredEmail] = useState("");
  const [resendState, setResendState] = useState("idle");
  const resendVerification = async () => {
    setResendState("sending");
    try {
      await api.post("/auth/resend-verification", { origin_url: window.location.origin, flow: "story_week" });
      setResendState("sent");
    } catch (e) { setResendState("err"); }
  };
  const clearRegistration = () => {
    setRegisteredEmail("");
    setResendState("idle");
    setMode("register");
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setForgotError("");
    const em = (forgotEmail || email || "").trim();
    if (!/^\S+@\S+\.\S+$/.test(em)) { setForgotError("Please enter a valid email."); return; }
    setForgotBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: em, origin_url: window.location.origin });
      setForgotSent(true);
    } catch (e) {
      const info = describeAuthError(e, "login");
      // Never leak whether the email exists — but do surface network/rate errors honestly.
      if (info.debug && (info.debug === "network" || info.debug === "offline" || info.debug.startsWith("429") || info.debug.startsWith("5"))) {
        setForgotError(info.message);
      } else {
        setForgotSent(true);
      }
    } finally {
      setForgotBusy(false);
    }
  };

  const finishAndGo = async () => {
    if (joinCode) {
      try { await api.post("/families/join", { code: joinCode }); } catch (e) { /* ignore */ }
    }
    // Explicit ?next= always wins (already sanitised by safeNext).
    if (nextUrl) { navigate(nextUrl); return; }
    // No explicit destination — route by profile.  Beta members (who have at
    // least one profile) go to the Full Living Book dashboard; Story Week
    // users go to /my-living-book.
    try {
      const me = await api.get("/auth/me");
      if ((me.data?.profile_count || 0) > 0) navigate("/dashboard");
      else navigate("/my-living-book");
    } catch (e) {
      navigate("/my-living-book");
    }
  };

  const validateRegister = () => {
    const errs = {};
    if (!name.trim()) errs.name = "Please tell us your name.";
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) errs.email = "Please enter a valid email.";
    if (!pwOk) errs.password = `Password must be at least ${PASSWORD_MIN} characters and include a letter and a number.`;
    if (!acceptedTerms) errs.terms = "Please agree to the Terms and Privacy Policy to continue.";
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateLogin = () => {
    const errs = {};
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) errs.email = "Please enter a valid email.";
    if (password.length < 1) errs.password = "Please enter your password.";
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setErrorDetail("");
    setErrorOfferSignIn(false);
    if (mode === "login" ? !validateLogin() : !validateRegister()) return;
    setBusy(true);
    try {
      if (mode === "login") {
        const res = await api.post("/auth/login", { email: email.trim(), password });
        if (res.data?.session_token) localStorage.setItem("session_token", res.data.session_token);
        // Hydrate the AuthContext from /auth/me so we always have the full,
        // canonical user object (email_verified, subscription tier, etc.).
        try { await checkAuth(); } catch (e) {
          setUser({ user_id: res.data.user_id, email: res.data.email, name: res.data.name, email_verified: !!res.data.email_verified });
        }
        await finishAndGo();
      } else {
        const tz = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; } catch { return "UTC"; } })();
        const res = await api.post("/auth/register", {
          email: email.trim(),
          password,
          name: name.trim(),
          marketing_consent: marketingConsent,
          timezone: tz,
          flow: isStoryWeekFlow ? "story_week" : null,
          origin_url: window.location.origin,
          terms_accepted: acceptedTerms,
          privacy_accepted: acceptedTerms,
        });
        if (res.data?.session_token) localStorage.setItem("session_token", res.data.session_token);
        setUser({ user_id: res.data.user_id, email: res.data.email, name: res.data.name, email_verified: !!res.data.email_verified });
        // Story Week: sign the user in and take them straight to Day 1.
        // The confirmation email is sent in the background — we never block
        // Question 1 on verification. The small "please confirm your email"
        // notice lives on the Day 1 screen instead.
        if (isStoryWeekFlow) {
          navigate("/story-week/day/1", { replace: true });
        } else {
          await finishAndGo();
        }
      }
    } catch (e) {
      const info = describeAuthError(e, mode);
      setError(info.message);
      setErrorDetail(info.debug);
      setErrorOfferSignIn(info.offerSignIn);
    } finally {
      setBusy(false);
    }
  };

  const inputBase = "w-full px-4 py-3 hairline-cream bg-white";
  const inputStyle = { borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "16px", minHeight: 48 };
  const labelClass = "block mb-2 text-[#1B2A4A]";
  const labelStyle = { fontFamily: "Georgia, serif", fontSize: "14px", fontWeight: 500 };
  const errorClass = "text-red-700 text-sm mt-2";

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#111D33" }} data-testid="login-page">
      <header className="px-6 sm:px-10 py-6">
        <Link to="/" className="inline-flex items-center gap-3" data-testid="login-home-link">
          <span className="text-[#B8973A] text-xl" style={{ fontFamily: "Cormorant Garamond, serif" }}>✦</span>
          <span className="text-[#E8D5A3]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.2rem", letterSpacing: "0.06em" }}>The Living Book</span>
        </Link>
      </header>
      <main className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-md p-8 sm:p-10 bg-[#FAF7F2]" style={{ borderRadius: 4 }} data-testid="login-card">
          {joinCode && (
            <div className="mb-6 p-4 bg-[#F7F0E0] text-center" style={{ border: "1px solid #E8D5A3", borderRadius: 4 }} data-testid="join-family-banner">
              <p className="label-ui text-[10px] text-[#B8973A] mb-1 tracking-[0.25em]">JOINING A FAMILY</p>
              <p className="text-[#1B2A4A] italic" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.05rem" }}>
                You&apos;ve been invited to a Living Book. Sign in or create your account — the family will be waiting.
              </p>
            </div>
          )}
          {registeredEmail ? (
            <div data-testid="check-email-screen">
              <p className="label-ui text-[11px] text-[#B8973A] text-center tracking-[0.25em]">A CONFIRMATION EMAIL IS ON ITS WAY</p>
              <h1 className="text-3xl sm:text-4xl text-center mt-4 mb-4" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                Check your email to begin
              </h1>
              <p className="text-[#1B2A4A]" style={{ fontFamily: "Georgia, serif", fontSize: "18px", lineHeight: 1.7 }}>
                We have sent a confirmation email to <strong>{registeredEmail}</strong>. Open that email and click the gold button marked &ldquo;Click here to confirm my email and begin Day One&rdquo;. It may take a few minutes. Please check your junk or spam folder if you cannot see it.
              </p>
              <div className="mt-8 space-y-3">
                <button
                  type="button"
                  onClick={resendVerification}
                  disabled={resendState === "sending"}
                  className="btn-gold w-full"
                  style={{ minHeight: 52, padding: "14px 20px", fontSize: "15px" }}
                  data-testid="resend-verification-btn"
                >
                  {resendState === "sent" ? "Sent — please check your inbox again" : resendState === "sending" ? "Sending…" : "Click here to resend the confirmation email"}
                </button>
                <button
                  type="button"
                  onClick={clearRegistration}
                  className="w-full py-3 hairline text-[#1B2A4A]"
                  style={{ background: "white", borderColor: "#E8D5A3", borderRadius: 4, minHeight: 48, fontFamily: "Georgia, serif", fontSize: "15px" }}
                  data-testid="correct-email-btn"
                >
                  Click here to correct my email address
                </button>
                <Link
                  to="/"
                  className="block text-center py-3 label-ui text-[12px] text-[#8C8278] hover:text-[#111D33] transition-colors"
                  style={{ minHeight: 48, lineHeight: "24px" }}
                  data-testid="back-home-link"
                >
                  Back to The Living Book homepage
                </Link>
              </div>
              {resendState === "err" && (
                <p className="mt-4 text-red-700 text-sm text-center">We couldn't resend just now. Please try once more in a minute.</p>
              )}
            </div>
          ) : (
          <>
          <p className="label-ui text-[11px] text-[#B8973A] text-center">
            {isBetaFlow ? "Founding Beta application" : (isStoryWeekFlow ? "A private family story app" : "Welcome")}
          </p>
          <h1 className="text-3xl sm:text-4xl text-center mt-4 mb-2" style={{ fontFamily: "Cormorant Garamond, serif" }}>
            {mode === "login" ? "Sign in" : (isBetaFlow ? <>Join the closed beta</> : (isStoryWeekFlow ? <>Create your free account</> : <>Begin your book</>))}
          </h1>
          <p className="text-center text-[#1B2A4A] mb-4" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.05rem", fontStyle: "italic" }}>
            {mode === "login"
              ? "Welcome back. Your Living Book is waiting."
              : isBetaFlow
                ? "First, create your free account. On the next screen you'll fill in your Founding Beta application."
                : isStoryWeekFlow
                  ? "Free account required. No card needed."
                  : "There is no rush. Memories surface when they're ready."}
          </p>
          {mode === "register" && isBetaFlow && (
            <div className="mb-6 p-4 text-left" style={{ background: "#FFF7E5", border: "1px solid #E8D5A3", borderRadius: 3 }} data-testid="beta-flow-explainer">
              <p className="text-[#1B2A4A]" style={{ fontFamily: "Georgia, serif", fontSize: "15px", lineHeight: 1.6 }}>
                <strong>What happens next:</strong> we create your free account, then take you to a short application (a few sentences about your family). We&rsquo;re inviting 20 families to the private test. Founding Member rate: &pound;49/year individual or &pound;89/year family (first 50). Beta founding round: &pound;79/&pound;129 (next 450).
              </p>
            </div>
          )}
          <div className="mb-4" />

          <button
            type="button"
            onClick={() => googleLogin(nextUrl)}
            className="w-full flex items-center justify-center gap-3 py-3 hairline transition-colors duration-500 hover:bg-[#F0EAE0]"
            style={{ borderRadius: 4, background: "white", borderColor: "#E8D5A3", minHeight: 48 }}
            data-testid="google-signin-btn"
          >
            <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.6 32.5 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.2 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.4-.1-2.7-.4-4z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.2 29.3 4 24 4 16.3 4 9.7 8.4 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.5-5.2l-6.2-5.2c-2 1.4-4.5 2.4-7.3 2.4-5.2 0-9.6-3.5-11.2-8.2l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.9 2.5-2.6 4.6-4.7 6.1l6.2 5.2c-.4.4 6.6-4.8 6.6-15.3 0-1.4-.1-2.7-.4-4z"/></svg>
            <span className="label-ui text-[12px] text-[#111D33]">Continue with Google</span>
          </button>

          <div className="flex items-center my-8 gap-4">
            <div className="flex-1 h-px bg-[#F0EAE0]" />
            <span className="label-ui text-[10px] text-[#8C8278]">or with email</span>
            <div className="flex-1 h-px bg-[#F0EAE0]" />
          </div>

          <form onSubmit={submit} className="space-y-5" data-testid="email-auth-form" noValidate>
            {mode === "register" && (
              <div>
                <label htmlFor="auth-name" className={labelClass} style={labelStyle}>Your name</label>
                <input
                  id="auth-name"
                  type="text"
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  aria-invalid={!!fieldErrors.name}
                  aria-describedby={fieldErrors.name ? "auth-name-error" : undefined}
                  className={inputBase}
                  style={inputStyle}
                  data-testid="auth-name-input"
                />
                {fieldErrors.name && <p id="auth-name-error" className={errorClass} data-testid="auth-name-error">{fieldErrors.name}</p>}
              </div>
            )}
            <div>
              <label htmlFor="auth-email" className={labelClass} style={labelStyle}>Email address</label>
              <input
                id="auth-email"
                type="email"
                autoComplete="email"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                aria-invalid={!!fieldErrors.email}
                aria-describedby={fieldErrors.email ? "auth-email-error" : undefined}
                className={inputBase}
                style={inputStyle}
                data-testid="auth-email-input"
              />
              {fieldErrors.email && <p id="auth-email-error" className={errorClass} data-testid="auth-email-error">{fieldErrors.email}</p>}
            </div>
            <div>
              <label htmlFor="auth-password" className={labelClass} style={labelStyle}>Password</label>
              <div style={{ position: "relative" }}>
                <input
                  id="auth-password"
                  type={showPassword ? "text" : "password"}
                  autoComplete={mode === "login" ? "current-password" : "new-password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={mode === "register" ? PASSWORD_MIN : 1}
                  aria-invalid={!!fieldErrors.password}
                  aria-describedby={fieldErrors.password ? "auth-password-error" : mode === "register" ? "password-rules" : undefined}
                  className={inputBase}
                  style={{ ...inputStyle, paddingRight: 52 }}
                  data-testid="auth-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                  className="absolute inset-y-0 right-0 flex items-center justify-center text-[#8C8278] hover:text-[#1B2A4A] transition-colors"
                  style={{ width: 48, background: "transparent", border: "none", cursor: "pointer" }}
                  data-testid="auth-password-toggle"
                >
                  {showPassword ? <EyeOff size={20} aria-hidden="true" /> : <Eye size={20} aria-hidden="true" />}
                </button>
              </div>
              {mode === "register" && (
                <ul id="password-rules" className="mt-2 space-y-1 text-sm" style={{ fontFamily: "Georgia, serif" }} data-testid="password-rules">
                  <li style={{ color: pwChecks.length ? "#4B7A2B" : "#8C8278" }}>
                    {pwChecks.length ? "✓" : "·"} At least {PASSWORD_MIN} characters
                  </li>
                  <li style={{ color: pwChecks.letter ? "#4B7A2B" : "#8C8278" }}>
                    {pwChecks.letter ? "✓" : "·"} Includes a letter
                  </li>
                  <li style={{ color: pwChecks.number ? "#4B7A2B" : "#8C8278" }}>
                    {pwChecks.number ? "✓" : "·"} Includes a number
                  </li>
                </ul>
              )}
              {fieldErrors.password && <p id="auth-password-error" className={errorClass} data-testid="auth-password-error">{fieldErrors.password}</p>}
            </div>

            {mode === "login" && (
              <div className="-mt-2" data-testid="forgot-password-block">
                {!forgotOpen ? (
                  <button
                    type="button"
                    onClick={() => { setForgotOpen(true); setForgotEmail(email); setForgotError(""); setForgotSent(false); }}
                    className="text-sm text-[#B8973A] hover:text-[#9d8030] underline underline-offset-4 transition-colors"
                    style={{ fontFamily: "Georgia, serif" }}
                    data-testid="forgot-password-btn"
                  >
                    Forgot your password?
                  </button>
                ) : forgotSent ? (
                  <div className="p-4" style={{ background: "#F7F0E0", border: "1px solid #E8D5A3", borderRadius: 4 }} data-testid="forgot-password-sent">
                    <p className="text-[#1B2A4A]" style={{ fontFamily: "Georgia, serif", fontSize: "15px", lineHeight: 1.6 }}>
                      If an account exists for that email, we've sent a link to choose a new password. Please check your inbox — and your spam folder if you can't see it. The link is safe to use for 60 minutes.
                    </p>
                    <button
                      type="button"
                      onClick={() => { setForgotOpen(false); setForgotSent(false); setForgotError(""); }}
                      className="mt-3 text-sm text-[#B8973A] hover:text-[#9d8030] underline underline-offset-4"
                      style={{ fontFamily: "Georgia, serif" }}
                      data-testid="forgot-password-close-btn"
                    >
                      Back to sign in
                    </button>
                  </div>
                ) : (
                  <div className="p-4" style={{ background: "#FAF7F2", border: "1px solid #E8D5A3", borderRadius: 4 }} data-testid="forgot-password-form">
                    <p className="text-[#1B2A4A] mb-3" style={{ fontFamily: "Georgia, serif", fontSize: "15px", lineHeight: 1.55 }}>
                      Enter your email and we'll send you a link to choose a new password.
                    </p>
                    <label htmlFor="forgot-email" className={labelClass} style={labelStyle}>Email address</label>
                    <input
                      id="forgot-email"
                      type="email"
                      autoComplete="email"
                      inputMode="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      className={inputBase}
                      style={inputStyle}
                      data-testid="forgot-email-input"
                    />
                    {forgotError && <p className={errorClass} data-testid="forgot-password-error">{forgotError}</p>}
                    <div className="mt-4 flex flex-col sm:flex-row gap-3">
                      <button
                        type="button"
                        onClick={submitForgot}
                        disabled={forgotBusy}
                        className="btn-gold"
                        style={{ minHeight: 48, padding: "12px 20px", fontSize: "14px" }}
                        data-testid="forgot-password-send-btn"
                      >
                        {forgotBusy ? "Sending…" : "Send me a reset link"}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setForgotOpen(false); setForgotError(""); }}
                        className="py-3 label-ui text-[12px] text-[#8C8278] hover:text-[#111D33] transition-colors"
                        style={{ minHeight: 48 }}
                        data-testid="forgot-password-cancel-btn"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {mode === "register" && (
              <>
                <div>
                  <label className="flex items-start gap-3 cursor-pointer" data-testid="terms-label">
                    <input
                      type="checkbox"
                      checked={acceptedTerms}
                      onChange={(e) => setAcceptedTerms(e.target.checked)}
                      className="mt-1 shrink-0"
                      style={{ width: 18, height: 18 }}
                      aria-invalid={!!fieldErrors.terms}
                      aria-describedby={fieldErrors.terms ? "auth-terms-error" : undefined}
                      data-testid="auth-terms-checkbox"
                    />
                    <span className="text-sm text-[#1B2A4A]" style={{ fontFamily: "Georgia, serif", lineHeight: 1.55 }}>
                      I agree to the{" "}
                      <Link to="/terms" target="_blank" rel="noopener" className="underline text-[#B8973A] hover:text-[#111D33]" data-testid="terms-link">Terms</Link>{" "}
                      and acknowledge the{" "}
                      <Link to="/privacy" target="_blank" rel="noopener" className="underline text-[#B8973A] hover:text-[#111D33]" data-testid="privacy-link">Privacy Policy</Link>.
                    </span>
                  </label>
                  {fieldErrors.terms && <p id="auth-terms-error" className={errorClass} data-testid="auth-terms-error">{fieldErrors.terms}</p>}
                </div>
                <div>
                  <label className="flex items-start gap-3 cursor-pointer" data-testid="marketing-label">
                    <input
                      type="checkbox"
                      checked={marketingConsent}
                      onChange={(e) => setMarketingConsent(e.target.checked)}
                      className="mt-1 shrink-0"
                      style={{ width: 18, height: 18 }}
                      data-testid="auth-marketing-checkbox"
                    />
                    <span className="text-sm text-[#1B2A4A]" style={{ fontFamily: "Georgia, serif", lineHeight: 1.55 }}>
                      Yes, I would also like occasional news and invitations from The Living Book. <span className="text-[#8C8278]">(optional)</span>
                    </span>
                  </label>
                </div>
              </>
            )}

            {error && (
              <div
                role="alert"
                data-testid="auth-error"
                className="p-4"
                style={{ background: "#FDECEC", border: "1px solid #E8B4B4", borderRadius: 4 }}
              >
                <p className="text-[#8A1F1F]" style={{ fontFamily: "Georgia, serif", fontSize: "15px", lineHeight: 1.55 }}>
                  {error}
                </p>
                {errorOfferSignIn && (
                  <button
                    type="button"
                    onClick={() => {
                      setMode("login");
                      setError("");
                      setErrorDetail("");
                      setErrorOfferSignIn(false);
                      setFieldErrors({});
                    }}
                    className="mt-3 inline-flex items-center px-4 py-2 bg-[#1B2A4A] text-[#E8D5A3] hover:bg-[#111D33] transition-colors"
                    style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "14px", minHeight: 40 }}
                    data-testid="auth-error-signin-btn"
                  >
                    Sign in instead
                  </button>
                )}
                {errorDetail && (
                  <p className="mt-2 label-ui text-[10px] text-[#8C8278] tracking-[0.15em]" data-testid="auth-error-detail">
                    Error reference: {errorDetail}
                  </p>
                )}
              </div>
            )}
            {mode === "register" && isStoryWeekFlow && (
              <p className="text-[#1B2A4A]" style={{ fontFamily: "Georgia, serif", fontSize: "15px", lineHeight: 1.7 }} data-testid="register-explainer">
                Creating your account takes about two minutes. We will email you a confirmation link. Click that link and Day One will open.
              </p>
            )}
            <button
              type="submit"
              disabled={busy}
              className="btn-primary w-full"
              style={{ minHeight: 52 }}
              data-testid="auth-submit-btn"
            >
              {busy ? "One moment…"
                : mode === "login"
                  ? "Click here to sign in"
                  : (isStoryWeekFlow ? "Click here to create my free account" : "Click here to begin my book")}
            </button>
            {mode === "register" && isStoryWeekFlow && (
              <p className="text-center text-[#8C8278]" style={{ fontFamily: "Georgia, serif", fontSize: "14px" }} data-testid="register-helper">
                Free account. No payment card needed.
              </p>
            )}
            <p className="text-center pt-2">
              <Link to="/" className="label-ui text-[12px] text-[#8C8278] hover:text-[#111D33] transition-colors" data-testid="login-back-home">
                Back to The Living Book homepage
              </Link>
            </p>
          </form>

          <p className="text-center text-[#1B2A4A] mt-8" style={{ fontFamily: "Georgia, serif", fontSize: "0.95rem" }}>
            {mode === "login" ? "New to The Living Book? " : "Already begun? "}
            <button
              type="button"
              onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); setErrorDetail(""); setErrorOfferSignIn(false); setFieldErrors({}); }}
              className="text-[#B8973A] hover:text-[#9d8030] transition-colors underline-offset-4 hover:underline"
              data-testid="auth-toggle-btn"
            >
              {mode === "login" ? "Create a free account" : "Sign in"}
            </button>
          </p>
          </>
          )}
        </div>
      </main>
      <footer className="py-8 text-center">
        <Ornament color="#B8973A" />
        <p className="mt-4 text-[#8C8278] italic text-sm" style={{ fontFamily: "Cormorant Garamond, serif" }}>
          Some books are written. Yours is lived.
        </p>
      </footer>
    </div>
  );
}
